import React, { useCallback, useEffect, useState } from "react";
import { Copy as CloneIcon, Lock, Pencil, Plus, Trash2 } from "lucide-react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { useNotice } from "../shared/useNotice";
import { AccessRole, AccessRoleInput, ocpiApi, OcpiPermission } from "../../../api/maintenance/ocpi";
import { canEditOcpi, errText, groupPermissions, Modal, MODULE_LABEL, PARTY_ROLE_LABEL } from "./ocpiShared";
import OcpiSubnav from "./OcpiSubnav";
import "../shared/maintenance-analytics.scss";
import "../stations/maintenance-stations.scss";
import "./ocpi.scss";

const PARTY_ROLES = ["EMSP", "NSP", "HUB", "NAP", "SCSP", "OTHER"];

const OcpiRoles: React.FC = () => {
  const [roles, setRoles] = useState<AccessRole[]>([]);
  const [catalog, setCatalog] = useState<OcpiPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useNotice("error", "");
  const [, setSuccess] = useNotice("success", "");
  const [editing, setEditing] = useState<{ role?: AccessRole; cloneFrom?: AccessRole } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([ocpiApi.roles(), ocpiApi.permissions()]);
      setRoles(r.data);
      setCatalog(c.data);
    } catch (e: any) {
      setError(errText(e, "Failed to load access roles"));
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => { load(); }, [load]);

  const remove = async (r: AccessRole) => {
    if (!window.confirm(`Delete the custom role "${r.name}"?`)) return;
    try {
      await ocpiApi.deleteRole(r.id);
      setSuccess("Role deleted");
      load();
    } catch (e: any) {
      setError(errText(e, "Failed to delete role"));
    }
  };

  const label = (perm: string) => {
    const p = catalog.find((c) => c.permission === perm);
    return p ? `${MODULE_LABEL[p.module] || p.module}: ${p.label}` : perm;
  };

  return (
    <MaintenanceLayout title="OCPI Roaming">
      <div className="ma">
        <OcpiSubnav />
        <div className="ma-note">
          An access role is a permission group plus default rate limits. Every partner gets exactly one role; built-in roles
          are templates (read-only — clone one to customise). Changing a custom role changes every partner that uses it.
        </div>
        <div className="ma-toolbar">
          <span className="ma-spacer" />
          {canEditOcpi() && <button className="ma-btn primary" onClick={() => setEditing({})}><Plus size={14} /> New custom role</button>}
        </div>
        {error && <div className="ma-error">{error}</div>}
        {loading ? <p className="ma-loading">Loading…</p> : (
          <div className="ma-grid">
            {roles.map((r) => (
              <div key={r.id} className="ma-card half">
                <div className="ma-card-h">
                  <h3>{r.is_system && <Lock size={13} style={{ marginRight: 4 }} />}{r.name}
                    {r.is_archived && <span className="ma-pill muted" style={{ marginLeft: 6 }}>archived</span>}</h3>
                  <span className="ma-hint">{r.partner_count} partner{r.partner_count === 1 ? "" : "s"}</span>
                </div>
                <div className="ma-card-b">
                  {r.description && <p style={{ marginTop: 0, fontSize: 13 }}>{r.description}</p>}
                  <div className="ocpi-chips">{r.permissions.length ? r.permissions.map((p) => <span key={p} className="on">{label(p)}</span>) : <span>no permissions</span>}</div>
                  <p className="ma-hint" style={{ marginBottom: 0 }}>
                    {r.requests_per_minute}/min, burst {r.burst} · {r.commands_per_minute ? `${r.commands_per_minute} commands/min` : "no remote commands"} ·
                    page size {r.max_page_size} · bodies {r.log_bodies ? "logged" : "not logged"}
                    {r.intended_party_roles.length ? ` · for ${r.intended_party_roles.map((x) => PARTY_ROLE_LABEL[x] || x).join(", ")}` : ""}
                  </p>
                  {canEditOcpi() && (
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <button className="ma-btn" onClick={() => setEditing({ cloneFrom: r })}><CloneIcon size={14} /> Clone</button>
                      {!r.is_system && <button className="ma-btn" onClick={() => setEditing({ role: r })}><Pencil size={14} /> Edit</button>}
                      {!r.is_system && r.partner_count === 0 && <button className="ma-btn" onClick={() => remove(r)}><Trash2 size={14} /> Delete</button>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {editing && (
        <RoleForm catalog={catalog} role={editing.role} cloneFrom={editing.cloneFrom} onClose={() => setEditing(null)}
                  onSaved={() => { setEditing(null); setSuccess("Role saved"); load(); }} />
      )}
    </MaintenanceLayout>
  );
};

const RoleForm: React.FC<{ catalog: OcpiPermission[]; role?: AccessRole; cloneFrom?: AccessRole; onClose: () => void; onSaved: () => void }> = ({
  catalog, role, cloneFrom, onClose, onSaved,
}) => {
  const base = role || cloneFrom;
  const [name, setName] = useState(role ? role.name : cloneFrom ? `${cloneFrom.name} (custom)` : "");
  const [description, setDescription] = useState(base?.description || "");
  const [perms, setPerms] = useState<string[]>(base?.permissions || []);
  const [partyRoles, setPartyRoles] = useState<string[]>(base?.intended_party_roles || []);
  const [lim, setLim] = useState({
    requests_per_minute: base?.requests_per_minute ?? 60, burst: base?.burst ?? 20,
    commands_per_minute: base?.commands_per_minute ?? 0, max_page_size: base?.max_page_size ?? 100,
  });
  const [logBodies, setLogBodies] = useState(base?.log_bodies ?? true);
  const [archived, setArchived] = useState(role?.is_archived ?? false);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const hasCommands = perms.some((p) => p.startsWith("commands:"));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    const data: AccessRoleInput = {
      name: name.trim(), description: description || null, permissions: perms, intended_party_roles: partyRoles,
      ...lim, log_bodies: logBodies,
    };
    try {
      if (role) await ocpiApi.updateRole(role.id, { ...data, is_archived: archived });
      else await ocpiApi.createRole({ ...data, clone_from_role_id: cloneFrom?.id ?? null });
      onSaved();
    } catch (e: any) {
      setErr(errText(e, "Failed to save role"));
      setSaving(false);
    }
  };

  return (
    <Modal title={role ? `Edit role — ${role.name}` : cloneFrom ? `Clone "${cloneFrom.name}"` : "New custom role"} onClose={onClose} wide>
      <form onSubmit={submit}>
        <div className="ocpi-form-grid">
          <label>Name<input value={name} onChange={(e) => setName(e.target.value)} minLength={2} maxLength={100} required /></label>
          <label>Description<input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} /></label>
        </div>
        <div className="ocpi-chips" style={{ margin: "10px 0" }} role="group" aria-label="Intended OCPI party roles">
          {PARTY_ROLES.map((r) => (
            <button type="button" key={r} className={`ma-tg ${partyRoles.includes(r) ? "on" : ""}`}
                    onClick={() => setPartyRoles((x) => x.includes(r) ? x.filter((y) => y !== r) : [...x, r])}>{PARTY_ROLE_LABEL[r]}</button>
          ))}
        </div>
        <div className="ocpi-perms">
          {groupPermissions(catalog).map((g) => (
            <div key={g.module} className="ocpi-perm-group">
              <h4>{MODULE_LABEL[g.module] || g.module}</h4>
              {g.items.map((p) => (
                <label key={p.permission} className={`ocpi-perm ${p.implemented ? "" : "disabled"}`}>
                  <input type="checkbox" checked={perms.includes(p.permission)}
                         onChange={(e) => setPerms((x) => e.target.checked ? [...x, p.permission] : x.filter((y) => y !== p.permission))} />
                  <div>
                    <div>{p.label}{!p.implemented && <span className="ma-pill muted" style={{ marginLeft: 6 }}>not yet supported</span>}</div>
                    <div className="ocpi-perm-desc">{p.description}</div>
                  </div>
                </label>
              ))}
            </div>
          ))}
        </div>
        <div className="ocpi-form-grid" style={{ marginTop: 12 }}>
          <label>Requests / minute<input type="number" min={1} value={lim.requests_per_minute} onChange={(e) => setLim({ ...lim, requests_per_minute: Number(e.target.value) })} /></label>
          <label>Burst<input type="number" min={1} value={lim.burst} onChange={(e) => setLim({ ...lim, burst: Number(e.target.value) })} /></label>
          <label>Commands / minute<input type="number" min={0} value={lim.commands_per_minute} onChange={(e) => setLim({ ...lim, commands_per_minute: Number(e.target.value) })} /></label>
          <label>Max page size<input type="number" min={1} max={1000} value={lim.max_page_size} onChange={(e) => setLim({ ...lim, max_page_size: Number(e.target.value) })} /></label>
          <label>Log bodies<select value={logBodies ? "on" : "off"} onChange={(e) => setLogBodies(e.target.value === "on")}><option value="on">On</option><option value="off">Off</option></select></label>
          {role && <label>Archived<select value={archived ? "yes" : "no"} onChange={(e) => setArchived(e.target.value === "yes")}><option value="no">No</option><option value="yes">Yes (hide for new partners)</option></select></label>}
        </div>
        {hasCommands && lim.commands_per_minute === 0 && <p className="maint-form-error">This role grants remote commands but allows 0 commands/minute.</p>}
        {err && <p className="maint-form-error">{err}</p>}
        <div className="maint-form-actions">
          <button type="button" className="maint-btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="maint-btn-primary" disabled={saving}>{saving ? "Saving…" : "Save role"}</button>
        </div>
      </form>
    </Modal>
  );
};

export default OcpiRoles;
